import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'lib/axios'
import { MainLayout } from 'views/layouts'
import { Button, StrokedButton } from 'views/components/UI/Buttons'
import ModalLoader from 'views/components/UI/ModalLoader'
import { useNotification } from 'lib/context/notification.context'

type QuestionnaireOption = {
  id: number
  text: string
  position: number
}

type QuestionnaireQuestion = {
  id: number
  text: string
  type?: string
  position: number
  is_required?: boolean
  options?: QuestionnaireOption[]
}

type QuestionnaireDetail = {
  id: number
  name: string
  description?: string
  questions?: QuestionnaireQuestion[]
}

type QuestionnaireAnswer = {
  question_id: number
  option_id?: number | null
  answer_text?: string | null
}

type QuestionnaireSubmission = {
  id: number
  status: string
  submitted_at?: string | null
}

type QuestionAnswerState = {
  optionIds: number[]
  answerText: string
}

type AnswerStateMap = Record<number, QuestionAnswerState>

const getQuestionType = (question: QuestionnaireQuestion) => {
  return question.type || 'short_text'
}

const isChoiceQuestion = (question: QuestionnaireQuestion) =>
  ['single_choice', 'multiple_choice'].includes(getQuestionType(question))

const buildAnswersState = (
  questions: QuestionnaireQuestion[],
  answers: QuestionnaireAnswer[],
): AnswerStateMap =>
  answers.reduce((acc, answer) => {
    const question = questions.find((item) => item.id === answer.question_id)
    if (!question) return acc

    const currentValue = acc[answer.question_id] || {
      optionIds: [],
      answerText: '',
    }

    if (isChoiceQuestion(question) && answer.option_id) {
      currentValue.optionIds = [...currentValue.optionIds, answer.option_id]
    }

    if (!isChoiceQuestion(question) && answer.answer_text) {
      currentValue.answerText = answer.answer_text
    }

    acc[answer.question_id] = currentValue
    return acc
  }, {} as AnswerStateMap)

const ProcessQuestionnaire = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { getError, getSuccess } = useNotification()
  const {
    questionnaireId,
    processQuestionnaireId,
    questionnaireName,
    processName,
    processId,
  } =
    (location.state as {
      questionnaireId?: number
      processQuestionnaireId?: number
      questionnaireName?: string
      processName?: string
      processId?: number
    }) || {}
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [questionnaire, setQuestionnaire] =
    useState<QuestionnaireDetail | null>(null)
  const [submission, setSubmission] =
    useState<QuestionnaireSubmission | null>(null)
  const [answers, setAnswers] = useState<AnswerStateMap>({})

  useEffect(() => {
    if (!questionnaireId || !processQuestionnaireId || !processId) {
      navigate('/', { replace: true })
      return
    }

    setLoading(true)

    Promise.all([
      axios.get(`/process-questionnaire/${processQuestionnaireId}`, {
        params: { process_id: processId },
      }),
      axios.get(`/company-questionnaire/${questionnaireId}/submission`, {
        params: { process_id: processId },
      }),
    ])
      .then(([questionnaireResponse, submissionResponse]) => {
        const nextQuestionnaire =
          questionnaireResponse?.data?.questionnaire || null
        const nextSubmission = submissionResponse?.data?.submission || null
        const nextAnswers = submissionResponse?.data?.answers || []

        setQuestionnaire(nextQuestionnaire)
        setSubmission(nextSubmission)
        setAnswers(
          buildAnswersState(nextQuestionnaire?.questions || [], nextAnswers),
        )
      })
      .catch(() => {
        getError('No fue posible cargar el cuestionario asignado')
        navigate('/', { replace: true })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [
    getError,
    navigate,
    processId,
    processQuestionnaireId,
    questionnaireId,
  ])

  const orderedQuestions = useMemo(
    () =>
      [...(questionnaire?.questions || [])].sort(
        (a, b) => a.position - b.position,
      ),
    [questionnaire?.questions],
  )

  const isSubmitted = submission?.status === 'submitted'

  const getAnswerState = (questionId: number) =>
    answers[questionId] || {
      optionIds: [],
      answerText: '',
    }

  const handleSingleChoiceChange = (
    questionId: number,
    optionId: number,
  ) => {
    setAnswers((current) => ({
      ...current,
      [questionId]: {
        optionIds: [optionId],
        answerText: '',
      },
    }))
  }

  const handleMultipleChoiceChange = (
    questionId: number,
    optionId: number,
  ) => {
    setAnswers((current) => {
      const currentValue = current[questionId] || {
        optionIds: [],
        answerText: '',
      }
      const optionIds = currentValue.optionIds.includes(optionId)
        ? currentValue.optionIds.filter((id) => id !== optionId)
        : [...currentValue.optionIds, optionId]

      return {
        ...current,
        [questionId]: {
          optionIds,
          answerText: '',
        },
      }
    })
  }

  const handleTextChange = (questionId: number, value: string) => {
    setAnswers((current) => ({
      ...current,
      [questionId]: {
        optionIds: [],
        answerText: value,
      },
    }))
  }

  const validateRequiredAnswers = () => {
    const firstMissingRequiredQuestion = orderedQuestions.find((question) => {
      if (!question.is_required) return false

      const currentAnswer = getAnswerState(question.id)
      if (isChoiceQuestion(question)) {
        return currentAnswer.optionIds.length === 0
      }

      return !currentAnswer.answerText.trim()
    })

    if (firstMissingRequiredQuestion) {
      getError(
        `Debes responder la pregunta ${firstMissingRequiredQuestion.position}`,
      )
      return false
    }

    return true
  }

  const buildPayloadAnswers = (): QuestionnaireAnswer[] =>
    orderedQuestions.flatMap<QuestionnaireAnswer>((question) => {
      const currentAnswer = getAnswerState(question.id)
      if (isChoiceQuestion(question)) {
        return currentAnswer.optionIds.map((optionId) => ({
          question_id: question.id,
          option_id: optionId,
        }))
      }

      if (!currentAnswer.answerText.trim()) {
        return []
      }

      return [
        {
          question_id: question.id,
          answer_text: currentAnswer.answerText.trim(),
        },
      ]
    })

  const handleSubmit = async () => {
    if (!questionnaireId || !processId) return
    if (!validateRequiredAnswers()) return

    setSubmitting(true)

    try {
      const response = await axios.post(
        `/company-questionnaire/${questionnaireId}/submission`,
        {
          process_id: processId,
          answers: buildPayloadAnswers(),
        },
      )

      setSubmission(response?.data?.submission || null)
      getSuccess('Cuestionario enviado correctamente')
      navigate('/', { replace: true })
    } catch (error) {
      getError('No fue posible guardar las respuestas del cuestionario')
    } finally {
      setSubmitting(false)
    }
  }

  const renderQuestionBody = (question: QuestionnaireQuestion) => {
    const questionType = getQuestionType(question)
    const currentAnswer = getAnswerState(question.id)

    if (
      questionType === 'single_choice' ||
      questionType === 'multiple_choice'
    ) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[...(question.options || [])]
            .sort((a, b) => a.position - b.position)
            .map((option) => (
              <label
                key={option.id}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  fontSize: 14,
                  color: '#1F2124',
                  cursor: isSubmitted ? 'default' : 'pointer',
                }}
              >
                <input
                  type={
                    questionType === 'multiple_choice' ? 'checkbox' : 'radio'
                  }
                  checked={currentAnswer.optionIds.includes(option.id)}
                  disabled={isSubmitted}
                  onChange={() =>
                    questionType === 'multiple_choice'
                      ? handleMultipleChoiceChange(question.id, option.id)
                      : handleSingleChoiceChange(question.id, option.id)
                  }
                />
                <span>{option.text}</span>
              </label>
            ))}
        </div>
      )
    }

    if (questionType === 'long_text') {
      return (
        <textarea
          disabled={isSubmitted}
          placeholder="Respuesta"
          value={currentAnswer.answerText}
          onChange={(event) =>
            handleTextChange(question.id, event.target.value)
          }
          style={{
            width: '100%',
            minHeight: 110,
            borderRadius: 12,
            border: '1px solid #D0D7DE',
            padding: 12,
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
        />
      )
    }

    return (
      <input
        disabled={isSubmitted}
        type={
          questionType === 'number'
            ? 'number'
            : questionType === 'date'
              ? 'date'
              : 'text'
        }
        placeholder="Respuesta"
        value={currentAnswer.answerText}
        onChange={(event) => handleTextChange(question.id, event.target.value)}
        style={{
          width: '100%',
          height: 44,
          borderRadius: 12,
          border: '1px solid #D0D7DE',
          padding: '0 12px',
          fontFamily: 'inherit',
        }}
      />
    )
  }

  return (
    <MainLayout>
      <div
        style={{
          maxWidth: 960,
          width: '100%',
          margin: '0 auto',
          padding: '32px 20px 48px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <div>
            <h2 style={{ fontSize: 32, margin: 0 }}>
              {questionnaire?.name || questionnaireName || 'Cuestionario'}
            </h2>
            {processName ? (
              <p style={{ marginTop: 8, color: '#5B6570' }}>
                Proceso: {processName}
              </p>
            ) : null}
          </div>
          <StrokedButton type="button" onClick={() => navigate(-1)}>
            Volver
          </StrokedButton>
        </div>

        {questionnaire?.description ? (
          <div
            style={{
              background: '#F7F9FB',
              borderRadius: 16,
              padding: 18,
              marginBottom: 20,
              color: '#364152',
            }}
          >
            {questionnaire.description}
          </div>
        ) : null}

        {isSubmitted ? (
          <div
            style={{
              background: '#E9F7EF',
              color: '#1E7A46',
              borderRadius: 16,
              padding: 16,
              marginBottom: 20,
              fontWeight: 600,
            }}
          >
            Este cuestionario ya fue enviado.
          </div>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {orderedQuestions.map((question, index) => (
            <section
              key={question.id}
              style={{
                background: '#FFFFFF',
                borderRadius: 18,
                padding: 20,
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    minWidth: 30,
                    height: 30,
                    borderRadius: 999,
                    background: '#E6F0F5',
                    color: '#24536B',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </div>
                <h3 style={{ margin: 0, fontSize: 18, color: '#1F2124' }}>
                  {question.text}
                </h3>
                {question.is_required ? (
                  <span
                    style={{
                      background: '#FFF4E5',
                      color: '#B45D00',
                      borderRadius: 999,
                      padding: '4px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Obligatoria
                  </span>
                ) : null}
              </div>
              {renderQuestionBody(question)}
            </section>
          ))}
        </div>

        {!isSubmitted ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 24,
            }}
          >
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || orderedQuestions.length === 0}
            >
              {submitting ? 'Guardando...' : 'Enviar cuestionario'}
            </Button>
          </div>
        ) : null}
      </div>
      <ModalLoader isLoading={loading || submitting} />
    </MainLayout>
  )
}

export default ProcessQuestionnaire
